// Ed25519 verify/sign for the call engine: WebCrypto when the runtime supports
// the Ed25519 algorithm (Chrome 113+/Safari 17+/Node 22), @noble/curves as the
// pure-JS fallback (docs/06 §3 names exactly this pair). Keys and signatures
// travel as raw bytes; base64 handling lives at the call sites.

import { ed25519 } from '@noble/curves/ed25519.js';

let webCryptoUsable: boolean | null = null;

async function tryWebCryptoVerify(
  publicKey: Uint8Array,
  message: Uint8Array,
  signature: Uint8Array,
): Promise<boolean | null> {
  if (webCryptoUsable === false) return null;
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) {
    webCryptoUsable = false;
    return null;
  }
  try {
    const key = await subtle.importKey('raw', toBuffer(publicKey), { name: 'Ed25519' }, false, [
      'verify',
    ]);
    const ok = await subtle.verify({ name: 'Ed25519' }, key, toBuffer(signature), toBuffer(message));
    webCryptoUsable = true;
    return ok;
  } catch {
    // Either Ed25519 is unsupported here (fall back permanently) or the key
    // bytes are invalid (report as unverified — noble will agree).
    if (webCryptoUsable === null) webCryptoUsable = false;
    return null;
  }
}

// WebCrypto rejects Uint8Arrays backed by SharedArrayBuffer at the type level;
// copy into a plain ArrayBuffer-backed view.
function toBuffer(bytes: Uint8Array): Uint8Array<ArrayBuffer> {
  const copy = new Uint8Array(new ArrayBuffer(bytes.length));
  copy.set(bytes);
  return copy;
}

/**
 * Verify an Ed25519 signature. Never throws — malformed key/signature material
 * is a verification failure, not an exception.
 */
export async function ed25519Verify(
  publicKey: Uint8Array,
  message: Uint8Array,
  signature: Uint8Array,
): Promise<boolean> {
  if (publicKey.length !== 32 || signature.length !== 64) return false;
  const viaWebCrypto = await tryWebCryptoVerify(publicKey, message, signature);
  if (viaWebCrypto !== null) return viaWebCrypto;
  try {
    return ed25519.verify(signature, message, publicKey);
  } catch {
    return false;
  }
}

/**
 * Sign with a raw 32-byte seed. The production identity key lives behind the
 * injected `IdentitySigner` (J7); this helper exists for the echo-test-free
 * parts of the engine that hold raw seeds (tests, local verification vectors).
 */
export function ed25519SignWithSeed(seed: Uint8Array, message: Uint8Array): Uint8Array {
  return ed25519.sign(message, seed);
}

/** Raw 32-byte public key for a raw 32-byte seed. */
export function ed25519PublicKeyFromSeed(seed: Uint8Array): Uint8Array {
  return ed25519.getPublicKey(seed);
}
